'use client'
import * as React from 'react'

import { tw } from '@/lib/utils'

import { useNavContext } from './nav-context-provider'
import { NavSvgDefs } from './nav-svg-defs'

const VISIBLE =
  'translate3d(0px, 0rem, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg, 0deg)'
const HIDDEN_MOBILE =
  'translate3d(0px, -11rem, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg, 0deg)'
const HIDDEN_DESKTOP =
  'translate3d(0px, -8rem, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg, 0deg)'

// useLayoutEffect warns during SSR; this component renders on the server.
const useIsoLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect

// NavWrapper owns the nav's POSITIONING and the scroll show/hide transform.
//
// Positioning is page-type-aware so the affiliate-disclosure bar — rendered in
// normal flow ABOVE this <nav> by ServerNav — can push the bar and the page
// content down by its REAL height, with no hardcoded padding:
//   - home: fixed — floats transparent over the hero (no disclaimer here).
//   - product / 404: relative — stays in flow and scrolls away with the page.
//   - everything else: sticky — sits below the disclaimer at rest and pins to
//     the top once the disclaimer scrolls past. Its parent ([data-segment])
//     spans the whole page, so it stays pinned for the entire scroll.
// While the mobile sheet is open the bar stays exactly where it is (the sheet
// measures the bar's bottom edge and rises to just below it), so nothing
// behind the sheet jumps. The one exception is a scroll-away bar that is
// already out of view when the sheet opens: it is pinned back to the top so
// its close button is reachable. The bar also lifts above the page's floating
// widgets and popups then, so it stays on top of the sheet's scrim; the
// desktop launcher's scrim gets the same lift.
//
// The transform (auto show/hide on scroll) lives here, on the SAME element as
// the positioning, so hiding slides the whole bar off-screen instead of
// leaving a transparent sticky strip behind that would swallow clicks.
export const NavWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pageType, isMenuVisible, isMobile, isMobileMenuVisible, current } = useNavContext()
  const navRef = React.useRef<HTMLElement>(null)
  const [pinForSheet, setPinForSheet] = React.useState(false)

  const mobileOverlayOpen = isMobile && (isMobileMenuVisible || !!current.containerId)
  // Both menus dim the page behind a scrim (z 1005); the bar lifts above it.
  const overlayOpen = mobileOverlayOpen || (!isMobile && !!current.containerId)
  const scrollsAway = pageType === 'product' || pageType === '404'

  useIsoLayoutEffect(() => {
    if (!mobileOverlayOpen || !scrollsAway) {
      setPinForSheet(false)
      return
    }
    const top = navRef.current?.getBoundingClientRect().top ?? 0
    setPinForSheet(top < 0)
  }, [mobileOverlayOpen, scrollsAway])

  const positionClass = () => {
    if (pageType === 'home') return 'fixed top-0'
    if (scrollsAway) return pinForSheet ? 'fixed top-0' : 'relative'
    return 'sticky top-0'
  }

  const transform = isMenuVisible ? VISIBLE : isMobile ? HIDDEN_MOBILE : HIDDEN_DESKTOP

  return (
    <nav
      ref={navRef}
      data-nav-bar
      className={tw(
        'w-full',
        overlayOpen ? 'z-[1010]' : 'z-[1000]',
        // The selection clears before the exit animation finishes. Keep the
        // retained launcher above its portalled scrim until it leaves the DOM.
        'has-[[data-nav-launcher]]:z-[1010]',
        positionClass(),
        isMenuVisible
          ? 'transition-transform duration-800 ease-[cubic-bezier(0.22,1,0.36,1)]'
          : 'transition-transform duration-300 ease-in'
      )}
      style={{ transformStyle: 'preserve-3d', transform }}
    >
      <NavSvgDefs />
      {children}
    </nav>
  )
}
